import React, { useEffect, useReducer, useRef, useState } from 'react';

import { Menu, MenuItem, TextField, Zoom } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { ToggleSwitch } from 'components/ToggleSwitch';
import styled from 'styled-components';
import { StyledBox } from 'styles/common';

import { generateKey } from 'constants/helper';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DotsIcon,
  JustifyIcon,
  ListIcon,
  PlusIcon2,
  RemoveIconV2,
  StyledIconV2,
} from 'constants/icons';

import {
  addJobFunctionDimension,
  deleteJobFunctionDimension,
  editJobFunctionDimension,
  moveJobFunctionDimension,
} from 'services/growth/admin';

import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';
import { Typography12 } from 'uiToolkit/Typography';

import { useJobFunctionsDataContext, useSelectedJobFunctionContext } from '../../GrowthSettings';
import {
  defaultDimensionName,
  OTHER_ACTIONS,
  RETRY_COUNT,
  TABLE_DATA_ACTIONS,
  TABLE_LAYOUT_ACTIONS,
} from '../GrowthTableContext';
import { StyledDimensionColumnCell } from '../GrowthTableHelperComps';
import { StyledTextArea } from './styledComps';

const CONTENT_TYPES = {
  PARAGRAPH: {
    id: 'PARAGRAPH',
    title: 'Paragraph',
    icon: <JustifyIcon size="fixed12px" />,
    disabled: false,
  },
  LIST: {
    id: 'LIST',
    title: 'List',
    icon: <ListIcon size="fixed12px" />,
    disabled: false,
  },
};

const CONTROL_OPTIONS = {
  MOVE_UP: 'MOVE_UP',
  MOVE_DOWN: 'MOVE_DOWN',
  ADD_DIMENSION: 'ADD_LEVEL',
  REMOVE_DIMENSION: 'REMOVE_LEVEL',
};

const defaultState = {
  dimensionName: '',
  competency: null,
};

const dimensionNameReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case 'UPDATE_DIMENSION_NAME': {
      return {
        dimensionName: payload.dimensionName,
        competency: state.competency && state.competency?.name !== payload.dimensionName ? null : state.competency,
      };
    }
    case 'UPDATE_COMPETENCY': {
      return {
        dimensionName: payload.competency.name,
        competency: payload.competency,
      };
    }
    default:
      return state;
  }
};

const DimensionName = ({ name, competency = null, onDimensionNameChange }) => {
  const isFirstRender = useRef(true);
  const ref = useRef(null);
  const [dimensionNameState, dimenstionNameDispatch] = useReducer(dimensionNameReducer, {
    dimensionName: competency?.name ?? name,
    competency,
  });
  const { competencies } = useJobFunctionsDataContext();

  const handleSubmit = () => {
    onDimensionNameChange({
      competency: dimensionNameState.competency,
      name: dimensionNameState.dimensionName,
    });
    if (ref?.current) ref.current.blur();
  };

  const handleInputChange = (ev, value, ...rest) => {
    if (ev) {
      dimenstionNameDispatch({
        type: 'UPDATE_DIMENSION_NAME',
        payload: {
          dimensionName: value,
        },
      });
    }
  };

  const handleChange = (ev, value, changeType) => {
    switch (changeType) {
      case 'select-option': {
        dimenstionNameDispatch({
          type: 'UPDATE_COMPETENCY',
          payload: {
            competency: value,
          },
        });
        // handleSubmit();
        break;
      }
      default:
        break;
    }
  };

  const handleKeyDown = (ev) => {
    if (ev.key === 'Enter') {
      ev.stopPropagation();
      ev.preventDefault();
      handleSubmit();
    }
  };

  const filterOptions = (options, state) => {
    const returnOptions = options.filter(({ name }) =>
      name.toLowerCase().includes(String(dimensionNameState.dimensionName).toLowerCase())
    );
    return returnOptions;
  };

  useEffect(() => {
    dimenstionNameDispatch({
      type: 'UPDATE_DIMENSION_NAME',
      payload: {
        dimensionName: name,
      },
    });
  }, [name]);

  useEffect(() => {
    if (competency) {
      dimenstionNameDispatch({
        type: 'UPDATE_COMPETENCY',
        payload: {
          competency,
        },
      });
    }
  }, [competency]);

  useEffect(() => {
    if (!isFirstRender.current && dimensionNameState.competency) {
      handleSubmit();
    }
  }, [dimensionNameState.competency]);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  return (
    <Autocomplete
      ref={ref}
      id="autocomplete"
      freeSolo
      fullWidth
      blurOnSelect
      value={dimensionNameState.competency}
      inputValue={dimensionNameState.dimensionName === defaultDimensionName ? '' : dimensionNameState.dimensionName} // have to get Suman's clearance on this
      onKeyDown={handleKeyDown}
      onInputChange={handleInputChange}
      onChange={handleChange}
      options={competencies}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.name ?? dimensionNameState.dimensionName}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          isTextNewDimension={dimensionNameState.dimensionName === defaultDimensionName}
          placeholder="Add Dimension"
          variant="outlined"
        />
      )}
      closeIcon={null}
    />
  );
};

const DimensionDescription = ({ description, onEdit }) => {
  const [localDescription, setLocalDescription] = useState(description);
  // const [error, setError] = useState('');

  const ref = useRef(null);

  const handleBlur = () => {
    onEdit(localDescription);
    if (ref?.current) ref.current.blur();
  };
  const handleInputChange = (ev, value) => {
    setLocalDescription(ev.target.value);
  };

  useEffect(() => {
    setLocalDescription(description);
  }, [description]);

  return (
    <StyledTextArea
      color="#999999"
      fontSize="14px"
      placeholder="Add description for dimension"
      width="100%"
      value={localDescription}
      onChange={handleInputChange}
      onBlur={handleBlur}
      rowsMin={1}
    />
  );
};

const DimensionContentType = ({ contentType, onChange }) => {
  const handleContentTypeChange = (contentTypeId) => {
    onChange(contentTypeId);
  };

  return (
    <ToggleSwitch
      selectedId={contentType}
      onIconClick={handleContentTypeChange}
      iconsList={Object.values(CONTENT_TYPES)}
    />
  );
};
/*
{
      uuid: '',
      name: 'Roles and Responsiblities',
      description: '',
      competencyId: '' | null,
      contentType: CONTENT_TYPE.LIST
    }
 */
const DimensionColumnCell = ({ index, isLoading, dimensionsCount, dimensionObj, layoutReducerDispatch }) => {
  const { selectedJobFunction } = useSelectedJobFunctionContext();
  const { uuid, name, description, competency, contentType } = dimensionObj;
  const [mouseOver, setMouseOver] = useState(false);

  const updateDimension = ({ name, description, contentType, competency }) => {
    let retryCount = RETRY_COUNT;
    const makeApiCall = () =>
      editJobFunctionDimension({
        uuid,
        name,
        description,
        isBullet: contentType === CONTENT_TYPES.LIST.id,
        competency,
        jobFunctionId: selectedJobFunction.uuid,
      })
        .then((res) => {})
        .catch((err) => {
          if (retryCount !== 0) {
            retryCount--;
            makeApiCall();
          } else {
            layoutReducerDispatch({
              type: OTHER_ACTIONS.SET_ERROR,
              payload: {},
            });
            console.error(err);
          }
        });

    makeApiCall();
  };

  const handleDescriptionEdit = (newDescription) => {
    layoutReducerDispatch({
      type: TABLE_DATA_ACTIONS.UPDATE_DIMENSION_DESCRIPTION,
      payload: {
        uuid,
        description: newDescription,
      },
    });
    updateDimension({ ...dimensionObj, description: newDescription });
  };

  const handleContentTypeChange = (contentTypeId) => {
    layoutReducerDispatch({
      type: TABLE_DATA_ACTIONS.UPDATE_DIMENSION_CONTENT_TYPE,
      payload: {
        uuid,
        contentType: contentTypeId,
      },
    });
    updateDimension({ ...dimensionObj, contentType: contentTypeId });
  };

  const handleDimensionNameChange = ({ competency, name }) => {
    layoutReducerDispatch({
      type: TABLE_DATA_ACTIONS.UPDATE_DIMENSION_NAME,
      payload: {
        name,
        uuid,
        competency,
      },
    });
    updateDimension({ ...dimensionObj, competency, name });
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMoreOptionsMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMoreOptionsMenuClose = (e, option) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleOptionSelect = (e, option) => {
    switch (option) {
      case CONTROL_OPTIONS.ADD_DIMENSION: {
        const feUuid = `dim-${generateKey(5)}`;
        layoutReducerDispatch({
          type: TABLE_LAYOUT_ACTIONS.ADD_DIMENSION,
          payload: { insertionIndex: index + 1, uuid: feUuid },
        });

        let retryCount = RETRY_COUNT;

        const makeApiCall = () =>
          addJobFunctionDimension({
            jobFunctionId: selectedJobFunction.uuid,
            insertAfterLevelId: uuid,
          })
            .then((res) => {
              layoutReducerDispatch({
                type: TABLE_LAYOUT_ACTIONS.REMOVE_DIMENSION_LOADING,
                payload: { feUuid, beUuid: res?.uuid, name: res?.name },
              });
            })
            .catch((err) => {
              if (retryCount !== 0) {
                retryCount--;
                makeApiCall();
              } else {
                layoutReducerDispatch({
                  type: OTHER_ACTIONS.SET_ERROR,
                  payload: {},
                });
                console.error(err);
              }
            });

        makeApiCall();
        break;
      }
      case CONTROL_OPTIONS.REMOVE_DIMENSION: {
        layoutReducerDispatch({
          type: TABLE_LAYOUT_ACTIONS.DELETE_DIMENSION_ROW,
          payload: { dimensionUuid: uuid },
        });

        let retryCount = RETRY_COUNT;
        const makeApiCall = () =>
          deleteJobFunctionDimension({
            jobFunctionId: selectedJobFunction.uuid,
            dimensionId: uuid,
          })
            .then((res) => {})
            .catch((err) => {
              if (retryCount !== 0) {
                retryCount--;
                makeApiCall();
              } else {
                layoutReducerDispatch({
                  type: OTHER_ACTIONS.SET_ERROR,
                  payload: {},
                });
                console.error(err);
              }
            });

        makeApiCall();
        break;
      }
      case CONTROL_OPTIONS.MOVE_UP: {
        layoutReducerDispatch({
          type: TABLE_LAYOUT_ACTIONS.MOVE_DIMENSION_ROW,
          payload: { currentIndex: index, moveToIndex: index - 1 },
        });

        if (index - 1 >= 0) {
          let retryCount = RETRY_COUNT;
          const makeApiCall = () =>
            moveJobFunctionDimension({
              jobFunctionId: selectedJobFunction.uuid,
              dimensionId: uuid,
              moveDirection: 'up',
            })
              .then((res) => {})
              .catch((err) => {
                if (retryCount !== 0) {
                  retryCount--;
                  makeApiCall();
                } else {
                  layoutReducerDispatch({
                    type: OTHER_ACTIONS.SET_ERROR,
                    payload: {},
                  });
                  console.error(err);
                }
              });

          makeApiCall();
        }

        break;
      }
      case CONTROL_OPTIONS.MOVE_DOWN: {
        layoutReducerDispatch({
          type: TABLE_LAYOUT_ACTIONS.MOVE_DIMENSION_ROW,
          payload: { currentIndex: index, moveToIndex: index + 1 },
        });
        if (index + 1 < dimensionsCount) {
          let retryCount = RETRY_COUNT;
          const makeApiCall = () =>
            moveJobFunctionDimension({
              jobFunctionId: selectedJobFunction.uuid,
              dimensionId: uuid,
              moveDirection: 'down',
            })
              .then((res) => {})
              .catch((err) => {
                if (retryCount !== 0) {
                  retryCount--;
                  makeApiCall();
                } else {
                  layoutReducerDispatch({
                    type: OTHER_ACTIONS.SET_ERROR,
                    payload: {},
                  });
                  console.error(err);
                }
              });
          makeApiCall();
        }

        break;
      }
      default:
        break;
    }
    handleMoreOptionsMenuClose(e);
  };

  return (
    <StyledDimensionColumnCell fixed onMouseEnter={() => setMouseOver(true)} onMouseLeave={() => setMouseOver(false)}>
      <StyledBox bottomGutter={2}>
        <DimensionName name={name} competency={competency} onDimensionNameChange={handleDimensionNameChange} />
      </StyledBox>
      <StyledBox flex="1" display="flex" alignItems="stretch" justifyContent="stretch" bottomGutter={1}>
        <DimensionDescription description={description} onEdit={handleDescriptionEdit} />
      </StyledBox>

      <StyledBox display="flex" alignItems="center" justifyContent="space-between">
        <DimensionContentType contentType={contentType} onChange={handleContentTypeChange} />
        <Zoom in={mouseOver && !isLoading}>
          <StyledBox>
            <CircularOutlineIcon onClick={handleMoreOptionsMenuOpen} color="primary" icon={StyledDotsIcon} />
            <StyledMenu
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 16,
              }}
              getContentAnchorEl={null}
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMoreOptionsMenuClose}
            >
              <StyledMenuItem onClick={(e) => handleOptionSelect(e, CONTROL_OPTIONS.MOVE_UP)}>
                <ChevronUpIcon size="fixed10px" containerSize="fixed24px" circularOutline color="primary" />
                <Typography12 color="primary" leftGutter={1.5}>
                  Move Up
                </Typography12>
              </StyledMenuItem>
              <StyledMenuItem onClick={(e) => handleOptionSelect(e, CONTROL_OPTIONS.MOVE_DOWN)}>
                <ChevronDownIcon size="fixed10px" containerSize="fixed24px" circularOutline color="primary" />
                <Typography12 leftGutter={1.5} color="primary">
                  Move Down
                </Typography12>
              </StyledMenuItem>
              <StyledMenuItem onClick={(e) => handleOptionSelect(e, CONTROL_OPTIONS.ADD_DIMENSION)}>
                <StyledIconV2 size="fixed10px" containerSize="fixed24px" circularOutline color="primary">
                  <PlusIcon2 />
                </StyledIconV2>
                <Typography12 color="primary" leftGutter={1.5}>
                  Add Dimension
                </Typography12>
              </StyledMenuItem>
              <StyledMenuItem onClick={(e) => handleOptionSelect(e, CONTROL_OPTIONS.REMOVE_DIMENSION)}>
                <RemoveIconV2 size="fixed10px" containerSize="fixed24px" circularOutline color="primary" />
                <Typography12 color="primary" leftGutter={1.5}>
                  Remove Dimension
                </Typography12>
              </StyledMenuItem>
            </StyledMenu>
          </StyledBox>
        </Zoom>
      </StyledBox>
    </StyledDimensionColumnCell>
  );
};

const StyledDotsIcon = styled(DotsIcon)`
  width: 13px;
  margin-top: 1px;
`;

const StyledMenu = styled(Menu)`
  ul {
    padding-top: 10px;
    padding-bottom: 10px;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  padding-right: 30px;
`;

const StyledTextField = styled(TextField)`
  input {
    color: ${(props) => (props.isTextNewDimension ? props.theme.palette.grey.main : 'inherit')};
    font-size: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    padding: 0 !important;
  }
  .MuiOutlinedInput-notchedOutline {
    border: none;
    outline: none;
  }
  .MuiOutlinedInput-root {
    padding: 0 !important;
  }
  .MuiAutocomplete-inputRoot {
    padding: 0 !important;
  }
`;

export default DimensionColumnCell;
